/** KPI карточки рекламной кампании и сверка «начислено / статистика».
 *
 *  Две разные суммы расхода, и обе показываются:
 *  - **начислено** — факт из финотчёта Ozon (витрина, `/summary?scope=obj:campaign:<id>`), по дате
 *    начисления; это деньги, которые Ozon удержал;
 *  - **расход по статистике** — из рекламного кабинета (`/campaign/{id}`, с НДС) по дате показа /
 *    заказа, плюс корректировки Ozon отдельной строкой.
 *  Они могут не совпадать: НДС, бонусы, корректировки, у «оплаты за заказ» — заказы, которые Ozon
 *  отменил или начислил позже. Расхождение больше 1% (`DIVERGE_REL`) — карточка показывает обе
 *  суммы и разницу явной плашкой, а не выбирает одну молча. */
import type { CampaignStats, OzCampaign, OzSummary } from "@/api/ozonAnalytics";
import { delta, deltaTone, shareDelta, type Kind } from "./calc";
import { int, intSigned, pct, pctSigned, pp, rubKop } from "./format";
import { moneyKpi, type Kpi } from "./kpi";

/** Порог расхождения «начислено / статистика»: доля от |статистики| (та же база, что у процента
 *  на плашке). */
export const DIVERGE_REL = 0.01;
/** Меньше полтинника — шум округлений, не расхождение. */
const DIVERGE_EPS = 0.5;

export const ACCRUED_HINT =
  "Факт из финотчёта Ozon (витрина): удержано по кампании с датой начисления в периоде";
export const STATS_HINT =
  "Из рекламного кабинета Ozon (с НДС) по дате показа / заказа; корректировки Ozon — отдельно";

/** Счётчик (показы, клики, заказы, штуки): больше — лучше. */
function countKpi(title: string, cur: number, was: number | null | undefined, hint: string): Kpi {
  const d = was === null || was === undefined ? null : delta(cur, was);
  return {
    title,
    cur: int(cur),
    curTitle: null,
    was: was === null || was === undefined ? null : int(was),
    wasTitle: null,
    deltaAbs: d ? intSigned(d.abs) : null,
    deltaRel: d ? pctSigned(d.rel) : null,
    tone: deltaTone(d?.abs),
    hint,
  };
}

/** Доля (CTR, ДРР): Δ в п.п.; `kind` expense — рост плохо (ДРР). Нет значения — «—». */
function ratioKpi(
  title: string,
  cur: number | null,
  was: number | null | undefined,
  hint: string,
  kind: Kind,
): Kpi {
  const hasCmp = was !== undefined;
  const diff = hasCmp ? shareDelta(cur, was ?? null) : null;
  return {
    title,
    cur: pct(cur),
    curTitle: cur === null ? "знаменатель 0 — не определено" : null,
    was: hasCmp ? pct(was ?? null) : null,
    wasTitle: null,
    deltaAbs: hasCmp ? pp(diff) : null,
    deltaRel: null,
    tone: deltaTone(diff, 0.0005, kind),
    hint,
  };
}

/** Цена клика: с копейками, рост — плохо. */
function cpcKpi(cur: number | null, was: number | null | undefined): Kpi {
  const hasCmp = was !== undefined;
  const d = hasCmp && cur !== null && was !== null ? delta(cur, was) : null;
  return {
    title: "CPC",
    cur: cur === null ? "—" : rubKop(cur),
    curTitle: cur === null ? "кликов нет — цена клика не определена" : null,
    was: hasCmp ? (was === null ? "—" : rubKop(was)) : null,
    wasTitle: null,
    deltaAbs: hasCmp ? (d ? rubKop(d.abs, true) : "—") : null,
    deltaRel: d ? pctSigned(d.rel) : null,
    tone: deltaTone(d?.abs, 0.005, "expense"),
    hint: "цена клика = расход по статистике / клики",
  };
}

/** Число из метрики статистики, которой у вида кампании нет, — ошибка контракта, а не 0. */
function need(v: number | null, what: string): number {
  if (v === null) throw new Error(`статистика кампании: нет метрики «${what}»`);
  return v;
}

/** KPI «Начислено Ozon» — из сводки по кампании; показывается и когда статистика не загрузилась. */
export function accruedKpi(s: OzSummary): Kpi {
  return moneyKpi(
    "Начислено Ozon", "expense", s.totals.cur.deductions, s.totals.cmp?.deductions, ACCRUED_HINT,
  );
}

/** KPI кампании: начислено (витрина) и расход по статистике — всегда; дальше — по виду
 *  кампании: «оплата за клик» — показы, клики, CTR, CPC, заказы, выручка по атрибуции, ДРР;
 *  «оплата за заказ» — заказы, штуки, сумма заказов, ДРР. Сравнение — если оно есть у сводки. */
export function campaignKpis(s: OzSummary, c: OzCampaign): Kpi[] {
  const out: Kpi[] = [accruedKpi(s)];
  const cur = c.totals.cur.stats;
  if (!cur) return out;
  // сравнения нет (или у периода сравнения нет статистики) — undefined: KPI без «было» и Δ
  const was = c.totals.cmp?.stats ?? null;
  const w = <K extends keyof CampaignStats>(k: K): CampaignStats[K] | undefined =>
    was ? was[k] : undefined;
  out.push(
    moneyKpi(
      "Расход по статистике",
      "expense",
      -cur.spend,
      w("spend") === undefined ? undefined : -(w("spend") as number),
      STATS_HINT,
    ),
  );
  if (c.type === "click") {
    out.push(
      countKpi("Показы", need(cur.views, "показы"), w("views"), "показы по статистике Ozon"),
      countKpi("Клики", need(cur.clicks, "клики"), w("clicks"), "клики по статистике Ozon"),
      ratioKpi("CTR", cur.ctr, w("ctr"), "клики / показы", "income"),
      cpcKpi(cur.cpc, w("cpc")),
      countKpi("Заказы", need(cur.orders, "заказы"), w("orders"), "заказы по атрибуции Ozon"),
      moneyKpi(
        "Выручка по атрибуции",
        "income",
        need(cur.revenue, "выручка"),
        w("revenue") ?? undefined,
        "выручка заказов, которые Ozon приписал кампании (не выручка витрины)",
      ),
      ratioKpi("ДРР", cur.drr, w("drr"), "расход по статистике / выручка по атрибуции", "expense"),
    );
  } else if (c.type === "order") {
    out.push(
      countKpi("Заказы", need(cur.orders, "заказы"), w("orders"), "заказы по отчёту «оплата за заказ»"),
      countKpi("Штуки", need(cur.qty, "штуки"), w("qty"), "штуки в этих заказах"),
      moneyKpi(
        "Сумма заказов",
        "income",
        need(cur.revenue, "сумма заказов"),
        w("revenue") ?? undefined,
        "сумма заказов по отчёту «оплата за заказ»",
      ),
      ratioKpi("ДРР", cur.drr, w("drr"), "расход по статистике / сумма заказов", "expense"),
    );
  }
  return out;
}

/** Сверка «начислено / статистика» за текущий период. `stats` — расход по статистике плюс
 *  корректировки (null — статистики по кампании нет). `diffPct` = diff / |stats| (null — статистики
 *  нет или она 0). `diverges` — |diff| не меньше полтинника и больше 1% от |stats|; статистика 0 —
 *  расхождение, если начислено ≠ 0 (не меньше полтинника). */
export interface AccrualCheck {
  accrued: number;
  spend: number | null;
  corrections: number;
  stats: number | null;
  diff: number | null;
  diffPct: number | null;
  diverges: boolean;
}

export function accrualCheck(s: OzSummary, c: OzCampaign): AccrualCheck {
  const accrued = -s.totals.cur.deductions;
  const st = c.totals.cur.stats;
  const corrections = c.totals.cur.corrections;
  if (!st) return { accrued, spend: null, corrections, stats: null, diff: null, diffPct: null, diverges: false };
  const stats = st.spend + corrections;
  const diff = accrued - stats;
  const diffPct = stats === 0 ? null : diff / Math.abs(stats);
  const diverges =
    Math.abs(diff) >= DIVERGE_EPS && (diffPct === null || Math.abs(diffPct) > DIVERGE_REL);
  return { accrued, spend: st.spend, corrections, stats, diff, diffPct, diverges };
}
